import { Component, Input, OnChanges } from '@angular/core';
import { PoChartSerie, PoChartType } from '@po-ui/ng-components';

import { formatPeriodDate, Period } from 'src/app/menu/smart-review/core/utils/period.util';
import { withSinglePointTooltip } from 'src/app/menu/smart-review/core/utils/po-chart-tooltip.util';
import { CostPoint } from '../../interfaces/costs.interface';
import {
  costToChartValue,
  currencySymbol,
  formatApiDateShort,
  formatCostAmount,
  formatCost,
  roundChartCostValue
} from '../../utils/costs-format.util';

/**
 * Acima disso o eixo X (uma categoria por dia) sobrepõe os rótulos de data e vira
 * ilegível — confirmado ao vivo com "Todo o período" (~54 dias). Vira semanal, não
 * um ajuste cosmético de rótulo: 200 dias em barras diárias continuariam ilegíveis
 * mesmo afinando fonte/ângulo.
 */
const WEEKLY_THRESHOLD = 31;

/** Primeira frase do aviso de agrupamento: o que o eixo passou a ser. */
const WEEKLY_NOTE = 'Por semana — o período tem dias demais para um a um.';

/**
 * Evolução do custo por dia (ou por semana, em períodos longos). Dias sem execução
 * não vêm do backend e, portanto, não aparecem no eixo — a série é dos dias que
 * tiveram consumo, não do calendário.
 */
@Component({
  selector: 'app-costs-timeseries-chart',
  templateUrl: './costs-timeseries-chart.component.html'
})
export class CostsTimeseriesChartComponent implements OnChanges {
  @Input() points: CostPoint[] = [];
  @Input() currency = 'USD';

  /**
   * O recorte da tela — é ele que diz quais baldes semanais são parciais.
   *
   * `points` sozinho não responde isso: dias sem execução não vêm do backend, então uma
   * semana com menos pontos tanto pode estar cortada pela ponta do período quanto ter
   * sido uma semana parada, e as duas leituras são opostas. Só o recorte separa as duas.
   */
  @Input() period?: Period;

  readonly columnType = PoChartType.Column;

  categories: string[] = [];
  series: PoChartSerie[] = [];
  weekly = false;

  /**
   * Texto do aviso de agrupamento — só existe em modo semanal. Além de anunciar o
   * agrupamento, avisa quando as colunas das pontas cobrem menos dias que as do meio:
   * sem isso, uma última semana cortada no meio lê como queda de gasto, que é o oposto
   * do que aconteceu.
   */
  groupingNote = '';

  /**
   * Categoria de maior custo, com o valor — "o que aconteceu nesse dia" é a primeira
   * pergunta ao ver um pico na barra. `values[i]` já é um número agregado (soma da
   * semana, em modo semanal), não a string original do backend — por isso passa de
   * novo por `formatCost` como estimativa, mesma exceção do `costToChartValue`.
   */
  peakLabel: string | null = null;

  ngOnChanges(): void {
    const points = this.points ?? [];
    this.weekly = points.length > WEEKLY_THRESHOLD;

    const [categories, values, weekStarts] = this.weekly ? weeklyBuckets(points) : dailyBuckets(points);
    this.categories = categories;
    // Os valores crus seguem para peakLabel e para o tooltip; só a série plotada é
    // arredondada. Arredondar antes fazia o rótulo do pico exibir US$ 0,01 onde o custo
    // real era US$ 0,0050 — o dobro, justamente na faixa em que a casa decimal importa.
    this.series = values.length
      ? [withSinglePointTooltip({
        label: this.costLabel,
        data: values.map(roundChartCostValue)
      }, chartTooltip(values))]
      : [];
    this.peakLabel = peakLabel(categories, values, this.currency);
    this.groupingNote = this.weekly ? groupingNote(weekStarts, this.period) : '';
  }

  get hasData(): boolean {
    return this.series.length > 0;
  }

  get costLabel(): string {
    return `Custo (${currencySymbol(this.currency)})`;
  }
}

/**
 * Categorias, valores e — só no agrupamento semanal — a segunda-feira de cada balde.
 * As datas de início vêm cruas (`YYYY-MM-DD`), e não como o rótulo já formatado, porque
 * é sobre elas que `groupingNote` compara as pontas do recorte.
 */
type Buckets = [categories: string[], values: number[], weekStarts: string[]];

function dailyBuckets(points: CostPoint[]): Buckets {
  return [
    points.map(point => formatApiDateShort(point.date)),
    points.map(point => costToChartValue(point.cost)),
    []
  ];
}

/**
 * Agrupa por semana (segunda a domingo) somando o custo dos dias dentro dela. A soma
 * aqui é a mesma exceção documentada em `costToChartValue`: alimenta só a geometria
 * do gráfico, nunca um total exibido — esse continua vindo do KPI, sobre a string.
 *
 * `new Date(ano, mês, dia)` a partir dos componentes já separados da string
 * `YYYY-MM-DD` é seguro (constrói meia-noite local) — o que quebraria seria parsear a
 * string inteira (`new Date(iso)`), que o JS interpreta como UTC e recua um dia em
 * UTC-3. Nunca chamamos isso aqui.
 */
function weeklyBuckets(points: CostPoint[]): Buckets {
  const order: string[] = [];
  const totals = new Map<string, number>();

  for (const point of points) {
    const date = parseApiDate(point.date);
    const mondayOffset = (date.getDay() + 6) % 7; // 0=segunda ... 6=domingo
    date.setDate(date.getDate() - mondayOffset);
    const weekStart = toApiDate(date);

    if (!totals.has(weekStart)) {
      order.push(weekStart);
      totals.set(weekStart, 0);
    }
    totals.set(weekStart, totals.get(weekStart)! + costToChartValue(point.cost));
  }

  const categories = order.map(weekStart => `Sem. ${formatApiDateShort(weekStart)}`);
  const values = order.map(weekStart => totals.get(weekStart)!);
  return [categories, values, order];
}

/**
 * O aviso do agrupamento semanal, mais a ressalva das pontas quando o recorte corta
 * uma semana no meio.
 *
 * A ressalva é sobre o alcance do gráfico, não sobre o gasto: a coluna da ponta é menor
 * porque cobre menos dias, e não porque se gastou menos. Sem dizer isso, uma consulta
 * feita numa terça mostra sempre a última coluna despencando.
 *
 * As comparações são entre strings `YYYY-MM-DD`, que ordenam como as datas que
 * representam — não há parsing nem fuso envolvido no caminho.
 *
 * Exportada (e com `today` como parâmetro) para o spec fixar o dia: o corte da última
 * semana depende de quando se olha, e um teste amarrado ao relógio passaria ou não
 * conforme o dia da semana em que rodasse.
 */
export function groupingNote(weekStarts: string[], period?: Period, today = formatPeriodDate(new Date())): string {
  if (!weekStarts.length) { return WEEKLY_NOTE; }

  // Sem `dateFrom` (preset "Todo o período") não há ponta inicial a cortar: o recorte
  // começa antes de qualquer dado, então a primeira semana está inteira por definição.
  const from = period?.dateFrom ?? '';

  // O fim é hoje quando não há `dateTo`, e nunca depois de hoje: um `dateTo` no futuro
  // (período personalizado) não traz dado que ainda não aconteceu, e tratá-lo como fim
  // real esconderia justamente a última semana parcial.
  const to = period?.dateTo && period.dateTo < today ? period.dateTo : today;

  const firstStart = weekStarts[0];
  const lastStart = weekStarts[weekStarts.length - 1];
  const firstEnd = addDays(firstStart, 6); // domingo do primeiro balde
  const lastEnd = addDays(lastStart, 6);   // domingo do último balde

  const firstPartial = !!from && from > firstStart;
  const lastPartial = to < lastEnd;

  // Cada ponta é anunciada pelo intervalo que a coluna REALMENTE cobre, não só pela data
  // em que o recorte termina: a coluna é rotulada pela segunda-feira dela ("Sem. 10/08"),
  // então dizer apenas "vai até 12/08" obriga o leitor a descobrir sozinho onde ela
  // começa. Com as duas datas, a frase encosta no rótulo do eixo.
  const first = `${formatApiDateShort(from)} a ${formatApiDateShort(firstEnd)}`;
  const last = `${formatApiDateShort(lastStart)} a ${formatApiDateShort(to)}`;

  if (firstPartial && lastPartial) {
    return `${WEEKLY_NOTE} As pontas cobrem menos dias: a primeira só ${first}, a última só ${last}.`;
  }
  if (lastPartial) {
    return `${WEEKLY_NOTE} A última cobre só ${last}.`;
  }
  if (firstPartial) {
    return `${WEEKLY_NOTE} A primeira cobre só ${first}.`;
  }
  return WEEKLY_NOTE;
}

/** `YYYY-MM-DD` como data local. `new Date(iso)` seria UTC e recuaria um dia em UTC-3. */
function parseApiDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Componentes locais (ano/mês/dia) de volta para `YYYY-MM-DD` — sem passar por UTC. */
function toApiDate(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Soma dias a uma data `YYYY-MM-DD`, de volta em `YYYY-MM-DD`. */
function addDays(value: string, days: number): string {
  const date = parseApiDate(value);
  date.setDate(date.getDate() + days);
  return toApiDate(date);
}

function peakLabel(categories: string[], values: number[], currency: string): string | null {
  if (!values.length) { return null; }
  let peakIndex = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i] > values[peakIndex]) { peakIndex = i; }
  }
  return `${categories[peakIndex]}: ${formatCost(values[peakIndex].toFixed(6), currency)}`;
}

function chartTooltip(values: number[]): string | undefined {
  return values.length === 1 ? formatCostAmount(values[0].toFixed(6)) : undefined;
}
